import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  Slider,
  Autocomplete,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { PlayerStats, Division } from "../types";
import { apiRequest } from "../config/api";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import { useAuth } from "../contexts/AuthContext";
import DivisionSelector from "../components/DivisionSelector";
import SeasonSelector from "../components/SeasonSelector";
import HeadToHeadDialog from "../components/HeadToHeadDialog";
import BadgeDisplay from "../components/BadgeDisplay";

const RANKINGS_PAGE_SIZE = 50; // Load 50 players at a time

// The public home page (PublicHome.tsx) ranks by hybrid at 40% volume; "Match Public" mirrors that
const PUBLIC_RANK_BY = 'hybrid';
const PUBLIC_PARTICIPATION_WEIGHT = 40;

function Rankings() {
  const { currentDivision } = useDivision();
  const { selectedSeason } = useSeason();
  const { isAdmin, isScorekeeper } = useAuth();
  const [rankings, setRankings] = useState<PlayerStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [h2hOpen, setH2hOpen] = useState(false);
  const [selectedPlayer1, setSelectedPlayer1] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedPlayer2, setSelectedPlayer2] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [comparingPlayerId, setComparingPlayerId] = useState<string | null>(
    null,
  );
  const [rankingMethod, setRankingMethod] = useState<'points' | 'winrate' | 'hybrid' | 'public'>('points');
  // "Match Public" what-if: visual only, never saved
  const [removeAllPenalties, setRemoveAllPenalties] = useState(false);
  const [removePenaltyPlayerIds, setRemovePenaltyPlayerIds] = useState<string[]>([]);
  const [penalizedPlayers, setPenalizedPlayers] = useState<{ id: string; name: string; points: number }[]>([]);
  const isPublicMode = rankingMethod === 'public';
  const [hideGuestPlayers, setHideGuestPlayers] = useState(true);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [participationWeight, setParticipationWeight] = useState<number>(50);
  const [showFormula, setShowFormula] = useState(false);
  
  // For infinite scroll detection
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log('[Rankings] useEffect triggered - division:', currentDivision, 'season:', selectedSeason?.name, 'seasonId:', selectedSeason?.id);
    // Reset and fetch new rankings
    setRankings([]);
    setOffset(0);
    setHasMore(true);
    fetchRankings(0, true);
  }, [currentDivision, selectedSeason, rankingMethod, participationWeight, removeAllPenalties, removePenaltyPlayerIds]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          console.log('[Rankings] Scroll threshold reached, loading more...');
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, loadingMore, offset]);

  const fetchRankings = async (currentOffset: number = 0, isInitial: boolean = false) => {
    if (!selectedSeason) {
      console.log('[Rankings] No selected season, skipping fetch');
      return;
    }

    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      if (isPublicMode) {
        // Same calculation as the public rankings; returns the full list in one go
        const previewParams = new URLSearchParams({
          division: currentDivision,
          seasonId: selectedSeason.id,
          rankBy: PUBLIC_RANK_BY,
          participationWeight: PUBLIC_PARTICIPATION_WEIGHT.toString(),
        });
        if (removeAllPenalties) {
          previewParams.set('excludePenalties', 'all');
        } else if (removePenaltyPlayerIds.length > 0) {
          previewParams.set('excludePenalties', removePenaltyPlayerIds.join(','));
        }
        const previewResponse = await apiRequest(
          `/api/stats/rankings/public-preview?${previewParams.toString()}`,
        );
        if (previewResponse.ok) {
          const result = await previewResponse.json();
          const rows: PlayerStats[] = result.data || [];
          setRankings(rows);
          setHasMore(false);
          setTotalPlayers(rows.length);
          setPenalizedPlayers(
            rows
              .filter((p) => (p.penaltyPoints || 0) > 0)
              .map((p) => ({ id: p.playerId, name: p.playerName, points: p.penaltyPoints || 0 })),
          );
        }
        return;
      }

      const params = new URLSearchParams({
        division: currentDivision,
        seasonId: selectedSeason.id,
        limit: RANKINGS_PAGE_SIZE.toString(),
        offset: currentOffset.toString(),
        rankBy: rankingMethod,
        participationWeight: participationWeight.toString(),
      });

      console.log('[Rankings] Fetching rankings with params:', params.toString());
      const response = await apiRequest(
        `/api/stats/rankings?${params.toString()}`,
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Check if response has pagination info (new format)
        if (result.data && result.pagination) {
          console.log('[Rankings] Received paginated data:', result.data.length, 'players, total:', result.pagination.total);
          
          if (isInitial) {
            setRankings(result.data);
          } else {
            setRankings(prev => [...prev, ...result.data]);
          }
          
          setTotalPlayers(result.pagination.total);
          setHasMore(result.pagination.hasMore);
          setOffset(currentOffset + result.data.length);
        } else {
          // Fallback for old format (no pagination)
          const data = Array.isArray(result) ? result : result.data || [];
          console.log('[Rankings] Received non-paginated data:', data.length, 'players');
          setRankings(data);
          setHasMore(false);
          setTotalPlayers(data.length);
        }
      }
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      console.log('[Rankings] Loading more from offset:', offset);
      fetchRankings(offset, false);
    }
  }, [offset, loadingMore, hasMore]);

  const getRankColor = (position: number) => {
    switch (position) {
      case 1:
        return "#FFD700"; // Gold
      case 2:
        return "#C0C0C0"; // Silver
      case 3:
        return "#CD7F32"; // Bronze
      default:
        return undefined;
    }
  };

  const getRankIcon = (position: number) => {
    if (position <= 3) {
      return <TrophyIcon sx={{ color: getRankColor(position), mr: 1 }} />;
    }
    return <span style={{ marginRight: 24 }}>{position}</span>;
  };

  const handleCompareClick = (playerId: string, playerName: string) => {
    if (!comparingPlayerId) {
      // First player selected
      setComparingPlayerId(playerId);
      setSelectedPlayer1({ id: playerId, name: playerName });
    } else if (comparingPlayerId === playerId) {
      // Same player clicked, cancel
      setComparingPlayerId(null);
      setSelectedPlayer1(null);
    } else {
      // Second player selected, open dialog
      setSelectedPlayer2({ id: playerId, name: playerName });
      setH2hOpen(true);
    }
  };

  const handleH2hClose = () => {
    setH2hOpen(false);
    setComparingPlayerId(null);
    setSelectedPlayer1(null);
    setSelectedPlayer2(null);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortedRankings = (rankingsToSort: PlayerStats[]) => {
    // Match Public keeps the server order so positions are exactly the public ones
    if (!sortColumn || isPublicMode) return rankingsToSort;

    return [...rankingsToSort].sort((a, b) => {
      // Always push 0-games players to the bottom when sorting descending
      if (sortDirection === 'desc') {
        if (a.gamesPlayed === 0 && b.gamesPlayed > 0) return 1;
        if (b.gamesPlayed === 0 && a.gamesPlayed > 0) return -1;
      }

      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'days':
          aValue = a.leagueDaysPlayed;
          bValue = b.leagueDaysPlayed;
          break;
        case 'winRate':
          aValue = a.bayesianWinRate || 0;
          bValue = b.bayesianWinRate || 0;
          break;
        case 'winPercentage':
          aValue = a.winPercentage || 0;
          bValue = b.winPercentage || 0;
          break;
        case 'matchWinRate':
          aValue = a.bayesianMatchWinRate || 0;
          bValue = b.bayesianMatchWinRate || 0;
          break;
        case 'matchWinPercentage':
          aValue = a.matchWinPercentage || 0;
          bValue = b.matchWinPercentage || 0;
          break;
        case 'hybrid':
          aValue = a.hybridScore || 0;
          bValue = b.hybridScore || 0;
          break;
        case 'points':
          aValue = a.totalPoints;
          bValue = b.totalPoints;
          break;
        case 'played':
          aValue = a.gamesPlayed;
          bValue = b.gamesPlayed;
          break;
        case 'won':
          aValue = a.gamesWon;
          bValue = b.gamesWon;
          break;
        case 'lost':
          aValue = a.gamesLost;
          bValue = b.gamesLost;
          break;
        case 'diff':
          aValue = a.gameDifference;
          bValue = b.gameDifference;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Filter rankings to exclude guest players if toggle is enabled, then apply sorting
  const filteredRankings = hideGuestPlayers && !isPublicMode
    ? rankings.filter(player => !player.isGuestPlayer)
    : rankings;
  
  const displayedRankings = getSortedRankings(filteredRankings);

  const renderSortableHeader = (label: string, column: string, tooltip?: string) => {
    const isActive = sortColumn === column;
    const content = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { opacity: 0.7 }
        }}
        onClick={() => handleSort(column)}
      >
        <span>{label}</span>
        {isActive && (
          sortDirection === 'desc' 
            ? <ArrowDownwardIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
            : <ArrowUpwardIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
        )}
      </Box>
    );
    
    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 0 } }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
      >
        Player Rankings
      </Typography>

      {/* Division Selector */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: { xs: 2, md: 3 },
          borderRadius: { xs: 1.5, md: 2 },
          border: "1px solid rgba(25, 118, 210, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 1, md: 2 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#1976d2",
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            🏆 Rankings for: {currentDivision}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              minWidth: { xs: "100%", sm: "auto" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <SeasonSelector fullWidth={true} excludeDrafts={true} />
            <DivisionSelector
              fullWidth={true}
              excludeTest={!isAdmin && !isScorekeeper}
            />
          </Box>
        </Box>
      </Paper>

      {/* Admin-only ranking method toggle */}
      {(isAdmin || isScorekeeper) && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={rankingMethod}
            exclusive
            onChange={(e, newMethod) => {
              if (newMethod !== null) {
                setRankingMethod(newMethod);
              }
            }}
            size="small"
            color="primary"
          >
            <ToggleButton value="points">
              🎯 By Points (Games Won)
            </ToggleButton>
            <ToggleButton value="winrate">
              📊 By Win Rate (Bayesian)
            </ToggleButton>
            <ToggleButton value="hybrid">
              ⚖️ Hybrid (Volume + Quality)
            </ToggleButton>
            <ToggleButton value="public">
              🌐 Match Public
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Match Public what-if: remove penalties to preview the effect (visual only) */}
          {isPublicMode && (
            <Box sx={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Exactly the public ranking calculation (hybrid, {PUBLIC_PARTICIPATION_WEIGHT}% volume / {100 - PUBLIC_PARTICIPATION_WEIGHT}% quality).
                The options below are a preview only and never change the public rankings.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={removeAllPenalties}
                    onChange={(e) => setRemoveAllPenalties(e.target.checked)}
                    color="warning"
                  />
                }
                label="Remove all penalties"
              />
              <Autocomplete
                multiple
                size="small"
                disabled={removeAllPenalties}
                options={penalizedPlayers}
                getOptionLabel={(o) => `${o.name} (−${o.points} pts)`}
                value={penalizedPlayers.filter((p) => removePenaltyPlayerIds.includes(p.id))}
                onChange={(_, selected) => setRemovePenaltyPlayerIds(selected.map((s) => s.id))}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="No penalised players in this division/season"
                renderInput={(params) => (
                  <TextField {...params} label="Remove penalties for specific players" />
                )}
              />
            </Box>
          )}

          {/* Hybrid weight slider - only shown when Hybrid is selected */}
          {rankingMethod === 'hybrid' && (
            <Box sx={{ width: '100%', maxWidth: 500, px: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Adjust Hybrid Balance: {participationWeight}% Volume (Total Wins) / {100 - participationWeight}% Quality (Win Rate)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="caption" sx={{ minWidth: 80 }}>
                  Volume
                </Typography>
                <Slider
                  value={participationWeight}
                  onChange={(e, value) => setParticipationWeight(value as number)}
                  min={0}
                  max={100}
                  step={10}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                  sx={{ flex: 1 }}
                />
                <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
                  Quality
                </Typography>
              </Box>
            </Box>
          )}
          
          {!isPublicMode && (
          <FormControlLabel
            control={
              <Switch
                checked={hideGuestPlayers}
                onChange={(e) => setHideGuestPlayers(e.target.checked)}
                color="primary"
              />
            }
            label="Hide Guest Players (from other divisions)"
          />
          )}
        </Box>
      )}

      <Typography
        variant="body1"
        color="text.secondary"
        paragraph
        sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
      >
        {isPublicMode
          ? 'Match Public: the same table players see on the public site. Use the penalty options to preview how penalties affected it.'
          : rankingMethod === 'points'
          ? 'Rankings based on total points (= games won). Ties broken by game difference.'
          : rankingMethod === 'winrate'
          ? 'Rankings based on Bayesian win rate (adjusted for consistency). Players with more games played have more reliable ratings.'
          : `Hybrid rankings: ${participationWeight}% volume (total games won) + ${100 - participationWeight}% quality (Bayesian win rate). ${
              participationWeight === 50 
                ? 'Balanced approach rewarding both quantity and consistency!' 
                : participationWeight > 50 
                  ? 'Emphasizes total wins (volume)!' 
                  : 'Emphasizes win percentage (quality)!'
            }`
        }
      </Typography>

      {/* Formula explanation accordion - shown for hybrid and winrate methods */}
      {(rankingMethod === 'hybrid' || rankingMethod === 'winrate') && (
        <Accordion 
          expanded={showFormula} 
          onChange={() => setShowFormula(!showFormula)}
          sx={{ mb: 2, backgroundColor: 'rgba(25, 118, 210, 0.03)' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="primary" fontSize="small" />
              <Typography variant="body2" fontWeight="medium" color="primary">
                {rankingMethod === 'hybrid' ? 'How Hybrid Ranking Works' : 'How Bayesian Win Rate Works'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {rankingMethod === 'hybrid' ? (
              <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Hybrid Score Formula:
                </Typography>
                <Box sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                  p: 2, 
                  borderRadius: 1, 
                  fontFamily: 'monospace',
                  mb: 2 
                }}>
                  Hybrid Score = (Volume Score × {participationWeight}%) + (Quality Score × {100 - participationWeight}%)
                </Box>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                  1. Volume Score (0-100):
                </Typography>
                <Box sx={{ pl: 2, mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Calculated per division using percentile ranking with 20-win minimum threshold:
                  </Typography>
                  <Box sx={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                    p: 1.5, 
                    borderRadius: 1, 
                    fontFamily: 'monospace',
                    fontSize: '0.85rem'
                  }}>
                    ((Your Total Wins - 20) / (Max Wins - 20)) × 100<br />
                    (Clamped to minimum of 0)
                  </Box>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    • 100 = Most total wins in division<br />
                    • 0 = 20 wins or fewer (baseline)<br />
                    • Rewards players who win more games total<br />
                    • Casual players (&lt;20 wins) get 0, not negative
                  </Typography>
                </Box>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  2. Quality Score (0-100):
                </Typography>
                <Box sx={{ pl: 2, mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Bayesian-adjusted game win rate:
                  </Typography>
                  <Box sx={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                    p: 1.5, 
                    borderRadius: 1, 
                    fontFamily: 'monospace',
                    fontSize: '0.85rem'
                  }}>
                    ((80 × Division Avg) + Games Won) / (80 + Games Played) × 100
                  </Box>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    • Confidence parameter: 80 games (~3-4 league days)<br />
                    • Prevents new players from reaching 100% with lucky wins<br />
                    • Pulls toward division average until sufficient games played
                  </Typography>
                </Box>

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                  Example:
                </Typography>
                <Box sx={{ pl: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Player:</strong> 55 total wins out of 100 games played<br />
                    <strong>Division:</strong> Max=120 wins, Avg win rate=52%, Threshold=20 wins<br />
                    <strong>Slider:</strong> {participationWeight}% volume / {100 - participationWeight}% quality
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
                    Volume = ((55-20)/(120-20)) × 100 = <strong>35</strong><br />
                    Quality = ((80×0.52)+55)/(80+100) × 100 = <strong>53.78</strong><br />
                    Hybrid = (35 × {participationWeight/100}) + (53.78 × {(100-participationWeight)/100}) = <strong>{(35 * participationWeight/100 + 53.78 * (100-participationWeight)/100).toFixed(2)}</strong>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Bayesian Win Rate Formula:
                </Typography>
                <Box sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                  p: 2, 
                  borderRadius: 1, 
                  fontFamily: 'monospace',
                  mb: 2 
                }}>
                  Bayesian Win Rate = ((C × D_avg) + Games Won) / (C + Games Played) × 100
                </Box>
                <Typography variant="body2" gutterBottom>
                  Where:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    • <strong>C</strong> = Confidence parameter = <strong>80</strong> (~3-4 league days)<br />
                    • <strong>D_avg</strong> = Division average win rate (e.g., 52%)<br />
                    • <strong>Games Won</strong> = Your total games won<br />
                    • <strong>Games Played</strong> = Your total games played
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Why Bayesian?</strong> It prevents players with only 1-2 games from showing unrealistic 100% or 0% win rates. 
                  New players start close to the division average and their rating becomes more accurate as they play more games.
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <Divider sx={{ mb: { xs: 2, md: 3 } }} />

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: { xs: 1.5, md: 2 },
          overflow: "auto",
        }}
      >
        <Table size={window.innerWidth < 600 ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 50, md: 80 },
                }}
              >
                Rank
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 1, md: 2 },
                  minWidth: { xs: 100, md: 150 },
                  position: "sticky",
                  left: 0,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                }}
              >
                Player Name
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 60, md: 100 },
                }}
              >
                {renderSortableHeader('Days', 'days')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 80, md: 120 },
                }}
              >
                {renderSortableHeader('Win Rate', 'winRate', 'Bayesian-adjusted game win rate (accounts for sample size)')}
              </TableCell>
              {(isAdmin || isScorekeeper) && (
                <TableCell
                  align="center"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                    minWidth: { xs: 70, md: 100 },
                  }}
                >
                  {renderSortableHeader('Win %', 'winPercentage', 'Simple game win percentage (games won / games played)')}
                </TableCell>
              )}
              {(isAdmin || isScorekeeper) && (
                <TableCell
                  align="center"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                    minWidth: { xs: 80, md: 110 },
                  }}
                >
                  {renderSortableHeader('M Win Rate', 'matchWinRate', 'Bayesian-adjusted match win rate')}
                </TableCell>
              )}
              {(isAdmin || isScorekeeper) && (
                <TableCell
                  align="center"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                    minWidth: { xs: 70, md: 100 },
                  }}
                >
                  {renderSortableHeader('M Win %', 'matchWinPercentage', 'Simple match win percentage')}
                </TableCell>
              )}
              {(isAdmin || isScorekeeper) && (
                <TableCell
                  align="center"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                    minWidth: { xs: 80, md: 110 },
                  }}
                >
                  {renderSortableHeader('Hybrid', 'hybrid', 'Hybrid score: 50% participation + 50% performance')}
                </TableCell>
              )}
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 70, md: 100 },
                }}
              >
                {renderSortableHeader('Points', 'points')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 60, md: 90 },
                }}
              >
                {renderSortableHeader('Played', 'played')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 50, md: 80 },
                }}
              >
                {renderSortableHeader('Won', 'won')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 50, md: 80 },
                }}
              >
                {renderSortableHeader('Lost', 'lost')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 70, md: 120 },
                }}
              >
                {renderSortableHeader('Diff', 'diff')}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  py: { xs: 1, md: 2 },
                  px: { xs: 0.5, md: 2 },
                  minWidth: { xs: 60, md: 100 },
                }}
              >
                Compare
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRankings.map((player, index) => (
              <TableRow
                key={player.playerId}
                sx={{
                  backgroundColor:
                    index < 3 ? `${getRankColor(index + 1)}20` : "inherit",
                  "&:hover": {
                    backgroundColor:
                      index < 3
                        ? `${getRankColor(index + 1)}30`
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {index <= 2 ? (
                      <TrophyIcon
                        sx={{
                          color: getRankColor(index + 1),
                          fontSize: { xs: 16, md: 20 },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: { xs: "0.8rem", md: "1rem" },
                          fontWeight: "bold",
                        }}
                      >
                        {index + 1}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 1, md: 2 },
                    position: "sticky",
                    left: 0,
                    backgroundColor:
                      index < 3 ? getRankColor(index + 1) : "background.paper",
                    zIndex: 1,
                    borderRight:
                      index < 3
                        ? `1px solid ${getRankColor(index + 1)}`
                        : "1px solid rgba(224, 224, 224, 1)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={index < 3 ? "bold" : "normal"}
                    sx={{
                      fontSize: { xs: "0.8rem", md: "1rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: { xs: 80, md: 150 },
                      color: index < 3 ? "white" : "inherit",
                    }}
                  >
                    {player.playerName}
                  </Typography>
                  {isPublicMode && (player.penaltyPoints || 0) > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: index < 3 ? 'white' : 'warning.main' }}
                    >
                      Penalty −{player.penaltyPoints}
                    </Typography>
                  )}
                  {isPublicMode && player.actualRank !== undefined && player.actualRank !== index + 1 && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 'bold',
                        color: index < 3 ? 'white' : player.actualRank > index + 1 ? 'success.main' : 'error.main',
                      }}
                    >
                      {player.actualRank > index + 1 ? '▲' : '▼'} {Math.abs(player.actualRank - (index + 1))} (public #{player.actualRank})
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    {player.leagueDaysPlayed}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Chip
                    label={`${player.bayesianWinRate?.toFixed(1) || '0.0'}%`}
                    color="primary"
                    size="small"
                    sx={{
                      fontSize: { xs: "0.7rem", md: "0.8rem" },
                      fontWeight: "bold",
                      height: { xs: 24, md: 28 },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ 
                      display: "block", 
                      fontSize: { xs: "0.65rem", md: "0.7rem" },
                      mt: 0.5
                    }}
                  >
                    ({player.gamesWon}/{player.gamesPlayed})
                  </Typography>
                </TableCell>
                {(isAdmin || isScorekeeper) && (
                  <TableCell
                    align="center"
                    sx={{
                      py: { xs: 1, md: 2 },
                      px: { xs: 0.5, md: 2 },
                    }}
                  >
                    <Chip
                      label={`${player.winPercentage?.toFixed(1) || '0.0'}%`}
                      color="secondary"
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: { xs: "0.7rem", md: "0.8rem" },
                        height: { xs: 24, md: 28 },
                      }}
                    />
                  </TableCell>
                )}
                {(isAdmin || isScorekeeper) && (
                  <TableCell
                    align="center"
                    sx={{
                      py: { xs: 1, md: 2 },
                      px: { xs: 0.5, md: 2 },
                    }}
                  >
                    <Chip
                      label={`${player.bayesianMatchWinRate?.toFixed(1) || '0.0'}%`}
                      color="success"
                      size="small"
                      sx={{
                        fontSize: { xs: "0.7rem", md: "0.8rem" },
                        fontWeight: "bold",
                        height: { xs: 24, md: 28 },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ 
                        display: "block", 
                        fontSize: { xs: "0.65rem", md: "0.7rem" },
                        mt: 0.5
                      }}
                    >
                      ({player.matchesWon}/{player.matchesWon + player.matchesLost})
                    </Typography>
                  </TableCell>
                )}
                {(isAdmin || isScorekeeper) && (
                  <TableCell
                    align="center"
                    sx={{
                      py: { xs: 1, md: 2 },
                      px: { xs: 0.5, md: 2 },
                    }}
                  >
                    <Chip
                      label={`${player.matchWinPercentage?.toFixed(1) || '0.0'}%`}
                      color="warning"
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: { xs: "0.7rem", md: "0.8rem" },
                        height: { xs: 24, md: 28 },
                      }}
                    />
                  </TableCell>
                )}
                {(isAdmin || isScorekeeper) && (
                  <TableCell
                    align="center"
                    sx={{
                      py: { xs: 1, md: 2 },
                      px: { xs: 0.5, md: 2 },
                    }}
                  >
                    <Chip
                      label={`${player.hybridScore?.toFixed(1) || '0.0'}`}
                      color="info"
                      size="small"
                      sx={{
                        fontSize: { xs: "0.7rem", md: "0.8rem" },
                        fontWeight: "bold",
                        height: { xs: 24, md: 28 },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Typography
                    sx={{ 
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      color: "text.secondary"
                    }}
                  >
                    {player.totalPoints}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    {player.gamesPlayed}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    {player.gamesWon}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    {player.gamesLost}
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Chip
                    label={
                      player.gameDifference >= 0
                        ? `+${player.gameDifference}`
                        : `${player.gameDifference}`
                    }
                    size="small"
                    color={
                      player.gameDifference > 0
                        ? "success"
                        : player.gameDifference === 0
                          ? "default"
                          : "error"
                    }
                    icon={
                      <TrendingUpIcon
                        sx={{ fontSize: { xs: "14px", md: "16px" } }}
                      />
                    }
                    sx={{
                      fontSize: { xs: "0.65rem", md: "0.75rem" },
                      height: { xs: 24, md: 32 },
                    }}
                  />
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1, md: 2 },
                    px: { xs: 0.5, md: 2 },
                  }}
                >
                  <Tooltip
                    title={
                      !comparingPlayerId
                        ? "Click to select for comparison"
                        : comparingPlayerId === player.playerId
                          ? "Click to cancel selection"
                          : "Click to compare with selected player"
                    }
                  >
                    <IconButton
                      size="small"
                      color={
                        comparingPlayerId === player.playerId
                          ? "primary"
                          : "default"
                      }
                      onClick={() =>
                        handleCompareClick(player.playerId, player.playerName)
                      }
                      sx={{
                        backgroundColor:
                          comparingPlayerId === player.playerId
                            ? "primary.light"
                            : "transparent",
                      }}
                    >
                      <CompareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {displayedRankings.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={(isAdmin || isScorekeeper) ? 13 : 9} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No player data available. Players will appear here after
                    completing matches.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {/* Infinite scroll loading indicator */}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={24} />
                    <Typography color="text.secondary">Loading more players...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Infinite scroll trigger (invisible element) */}
      <div ref={observerTarget} style={{ height: '20px', margin: '10px 0' }} />
      
      {/* Load more button (fallback for manual loading) */}
      {hasMore && !loading && displayedRankings.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
          <Button 
            variant="outlined" 
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : `Load More (${displayedRankings.length} of ${hideGuestPlayers ? displayedRankings.length : totalPlayers} shown)`}
          </Button>
        </Box>
      )}
      
      {!hasMore && displayedRankings.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing all {displayedRankings.length} players{hideGuestPlayers ? ' (guests hidden)' : ''}
          </Typography>
        </Box>
      )}

      {rankings.length > 0 && (
        <Box mt={3}>
          <Typography variant="body2" color="text.secondary">
            Rankings are updated automatically after each match completion.
            Total Points = Games Won. Ties are broken by Game Difference (Games
            Won - Games Lost).
          </Typography>
        </Box>
      )}

      {/* Head-to-Head Dialog */}
      {selectedPlayer1 && selectedPlayer2 && (
        <HeadToHeadDialog
          open={h2hOpen}
          onClose={handleH2hClose}
          player1Id={selectedPlayer1.id}
          player2Id={selectedPlayer2.id}
          player1Name={selectedPlayer1.name}
          player2Name={selectedPlayer2.name}
          seasonId={selectedSeason?.id}
        />
      )}
    </Box>
  );
}

export default Rankings;
